import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuthData } from '../hooks/useAuthData';
import { useSocketSetup } from '../hooks/useSocketSetup';
import { performLogout } from '../stores/auth.store';
import { getStoredToken } from '../utils/authSession';

/**
 * AuthProvider — Zirro-style route guard.
 *
 * Key differences from the old implementation:
 *
 * 1. NO synchronous render-time `<Navigate>` redirect.
 *    The old pattern (`if (!isLoading && !isAuthenticated) return <Navigate>`)
 *    fired on render #1 before any useEffect ran, so `user` was always null and
 *    `isAuthenticated` was always false — causing immediate logout on every reload.
 *
 * 2. Logout is handled in a `useEffect` with a ref guard (same as Zirro's
 *    `hasCalledLogoutRef`) so it only fires once and never on the first
 *    synchronous render.
 *
 * 3. Socket lifecycle is owned here via `useSocketSetup()` — no module-level
 *    side effects or circular dependencies.
 */
export const AuthProvider = () => {
  const location = useLocation();
  const { isLoading, shouldLogout } = useAuthData();
  const hasCalledLogoutRef = useRef(false);

  // Initialise / teardown socket connection for the lifetime of this provider
  useSocketSetup();

  useEffect(() => {
    // getStoredToken checks admin_token, assistant_token, and user_token
    const token = getStoredToken();

    if ((!token || shouldLogout) && !hasCalledLogoutRef.current) {
      hasCalledLogoutRef.current = true;
      performLogout();
    }
  }, [location.pathname, shouldLogout]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#61013B]" />
      </div>
    );
  }

  return <Outlet />;
};
